import { Fragment, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { useSubscription } from '@meraki-internal/state';
import { IonToggle } from '@ionic/react';
import { TextField } from '../../components/TextField';
import { TextArea } from '../../components/TextArea';
import { SearchableSelectField } from '../../components/SearchableSelectField';
import { EnumSelectField } from './EnumSelectField';
import { IStructuredFieldDef, IStructuredRecordDescriptor } from './StructuredRecordDescriptor';
import { StructuredRecordFormState } from './StructuredRecordFormState';

const useStyles = createUseStyles({
    form: {
        gap: 20,
        display: 'flex',
        flexDirection: 'column'
    }
});

export interface IStructuredRecordEditorProps<T> {
    descriptor: IStructuredRecordDescriptor<T>;
    formState: StructuredRecordFormState<T>;
    id: string;
    readOnly?: boolean;
    reportValidity: (valid: boolean) => void;
}

export const StructuredRecordEditor = <T,>({ descriptor, formState, id, readOnly, reportValidity }: IStructuredRecordEditorProps<T>) => {
    const classes = useStyles();

    useSubscription(() => formState);

    const record = formState.state.record;

    const checkValidity = () => {
        reportValidity(Boolean(record && descriptor.getName(record)));
    };

    useEffect(() => {
        formState.init(id);

        return () => {
            formState.clear();
        };
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        checkValidity();
    }, [record]); // eslint-disable-line react-hooks/exhaustive-deps

    // Field defs are config-driven (`field.key`/`field.valueKey` are `keyof T`, but the value
    // coming back from a rendered atom -- a string from TextField, a boolean from IonToggle --
    // can't be statically correlated to T's real per-property type since T is fully generic
    // here). This is the one deliberate boundary cast in the generic editor; every call site
    // below only ever sets values under real field.key/valueKey entries from this record's own
    // descriptor, so the cast is safe in practice.
    const patchFields = (values: Record<string, unknown>) => {
        formState.patch(values as Partial<T>);
    };

    const renderField = (field: IStructuredFieldDef<T>) => {
        const name = String(field.key);
        const rawValue = record?.[field.key];
        const stringValue = rawValue === undefined || rawValue === null ? undefined : String(rawValue);

        if (field.render) {
            // requires an actual record -- render nothing on the one frame before formState.init
            // resolves it, same as every atom below implicitly tolerates via stringValue/rawValue.
            return record ? (
                <Fragment key={name}>
                    {field.render({ record, readOnly: Boolean(readOnly), patch: formState.patch })}
                </Fragment>
            ) : null;
        }

        switch (field.type) {
            case 'text':
                return (
                    <TextField
                        key={name}
                        name={name}
                        readOnly={readOnly}
                        required={field.required}
                        value={stringValue}
                        label={field.label}
                        onInput={(val) => patchFields({ [field.key]: val })}
                    />
                );

            case 'textarea':
                return (
                    <TextArea
                        key={name}
                        rows={3}
                        name={name}
                        readOnly={readOnly}
                        value={stringValue}
                        label={field.label}
                        onInput={(val) => patchFields({ [field.key]: val })}
                    />
                );

            case 'searchable-select': {
                const options = (field.getSelectOptions?.() || []).map(option => ({ value: option.id, label: option.label }));
                const rawValueKey = field.valueKey && record ? record[field.valueKey] : undefined;
                const selected = stringValue ? {
                    value: rawValueKey === undefined || rawValueKey === null ? undefined : String(rawValueKey),
                    label: stringValue
                } : undefined;

                return (
                    <SearchableSelectField
                        key={name}
                        data-type={name}
                        disabled={readOnly}
                        required={field.required}
                        label={field.label}
                        options={options}
                        selected={selected}
                        onChange={(newlySelected) => {
                            const patch: Record<string, unknown> = { [field.key]: newlySelected?.label };
                            if (field.valueKey) {
                                patch[String(field.valueKey)] = newlySelected?.value;
                            }
                            patchFields(patch);
                        }}
                    />
                );
            }

            case 'enum-select':
                return (
                    <EnumSelectField
                        key={name}
                        data-id={name}
                        readOnly={readOnly}
                        label={field.label}
                        value={stringValue}
                        options={field.enumOptions || []}
                        onChange={(val) => patchFields({ [field.key]: val })}
                    />
                );

            case 'toggle':
                return (
                    <IonToggle
                        key={name}
                        data-id={name}
                        checked={Boolean(rawValue)}
                        disabled={readOnly}
                        onIonChange={(e) => patchFields({ [field.key]: e.detail.checked })}
                    >
                        {field.label}
                    </IonToggle>
                );

            case 'date':
                // approximate date (e.g. month/year) -- free text is acceptable, so this reuses
                // TextField rather than introducing a dedicated date-picker atom.
                return (
                    <TextField
                        key={name}
                        name={name}
                        readOnly={readOnly}
                        value={stringValue}
                        label={field.label}
                        onInput={(val) => patchFields({ [field.key]: val })}
                    />
                );
        }
    };

    return (
        <div data-type="structured-record-editor" className={classes.form} style={readOnly ? {gap: 30} : {}}>
            {descriptor.fields.map(field => renderField(field))}
        </div>
    );
};
